import React from 'react';
import { X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { STATUS } from '@/api/admissions';

const TONES: Record<string, string> = {
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

export function Pill({ tone = 'slate', children }: { tone?: string; children: React.ReactNode }) {
  return <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${TONES[tone] ?? TONES.slate}`}>{children}</span>;
}

export function StatusPill({ status }: { status: string }) {
  const s = STATUS[status] ?? { label: status, tone: 'slate' };
  return <Pill tone={s.tone}>{s.label}</Pill>;
}

export function Stat({ label, value, sub, tone }: { label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: 'amber' | 'green' | 'red' }) {
  const c = tone === 'amber' ? 'text-amber-600' : tone === 'green' ? 'text-green-700' : tone === 'red' ? 'text-red-600' : 'text-slate-900';
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 leading-tight">{label}</p>
      <p className={`text-2xl font-extrabold mt-1 ${c}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export function Card({ title, sub, action, children, pad = true }: { title?: React.ReactNode; sub?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; pad?: boolean }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {(title || action) && (
        <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="min-w-0">{title && <p className="font-bold text-slate-900">{title}</p>}{sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}</div>
          {action}
        </div>
      )}
      <div className={pad ? 'p-4 sm:p-5' : ''}>{children}</div>
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}

export const input = 'w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-600/20 focus:border-green-600';

export function Btn({ kind = 'primary', className = '', ...p }: React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: 'primary' | 'dark' | 'ghost' | 'danger' | 'soft' }) {
  const k = {
    primary: 'bg-green-600 hover:bg-green-700 text-white',
    dark: 'bg-[#0f172a] hover:bg-slate-800 text-white',
    ghost: 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    soft: 'bg-slate-100 hover:bg-slate-200 text-slate-800',
  }[kind];
  return <button {...p} className={`inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition disabled:opacity-50 ${k} ${className}`} />;
}

export function Modal({ title, onClose, children, wide, footer }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean; footer?: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className={`bg-white w-full ${wide ? 'sm:max-w-2xl' : 'sm:max-w-md'} rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 shrink-0">
          <h3 className="font-bold text-slate-900 truncate">{title}</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
        {footer && <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex gap-2 justify-end shrink-0">{footer}</div>}
      </div>
    </div>
  );
}

/** Slide-in panel on desktop, full-screen sheet on phones. */
export function Drawer({ title, sub, onClose, children }: { title: React.ReactNode; sub?: React.ReactNode; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[55] bg-slate-900/40 flex justify-end" onClick={onClose}>
      <div className="bg-slate-50 w-full sm:max-w-xl h-full flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0"><div className="font-bold text-slate-900 text-lg truncate">{title}</div>{sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

export function Notice({ tone, children, onClose }: { tone: 'ok' | 'err' | 'info'; children: React.ReactNode; onClose?: () => void }) {
  const c = tone === 'ok' ? 'bg-green-50 border-green-200 text-green-800' : tone === 'err' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-900';
  return (
    <div className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${c}`}>
      <span className="flex gap-2">{tone === 'ok' ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}<span>{children}</span></span>
      {onClose && <button onClick={onClose} aria-label="Dismiss"><X className="w-4 h-4" /></button>}
    </div>
  );
}

export function Empty({ icon: Icon, children }: { icon?: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <div className="text-center text-sm text-slate-500 py-10 px-4">
      {Icon && <Icon className="w-7 h-7 mx-auto mb-2 text-slate-300" />}
      {children}
    </div>
  );
}

export function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="w-full flex items-start gap-3 text-left py-2">
      <span className={`mt-0.5 w-9 h-5 rounded-full relative shrink-0 transition ${checked ? 'bg-green-600' : 'bg-slate-300'}`}>
        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition ${checked ? 'left-[18px]' : 'left-0.5'}`} />
      </span>
      <span><span className="block text-sm font-semibold text-slate-900">{label}</span>{hint && <span className="block text-xs text-slate-500">{hint}</span>}</span>
    </button>
  );
}

export const fmtDate = (s?: string | null, time = false) => s ? new Date(s).toLocaleString('en-GB', time ? { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' } : { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
